"use client";

/**
 * Helper hook with utility functions for the TradedNews application
 */
export function useHelper() {
  /**
   * Format dates in a readable way, handling various date formats from Firestore
   * @param date Any date format (Timestamp, Date, number, string)
   * @returns Formatted date string
   */
  const formatDate = (date: any): string => {
    if (!date) return 'N/A';
    
    try {
      // Handle Firestore Timestamp objects
      if (date && typeof date === 'object' && 'toDate' in date && typeof date.toDate === 'function') {
        return date.toDate().toLocaleString();
      }
      
      // Handle Date objects and ISO strings
      if (date instanceof Date) {
        return date.toLocaleString();
      }
      
      // Handle numeric timestamps (seconds or milliseconds)
      if (typeof date === 'number') {
        // If timestamp is in seconds (Firestore), convert to milliseconds
        const timestamp = date < 10000000000 ? date * 1000 : date;
        return new Date(timestamp).toLocaleString();
      }
      
      // Handle string dates
      if (typeof date === 'string') {
        const dateObj = new Date(date);
        if (!isNaN(dateObj.getTime())) {
          return dateObj.toLocaleString();
        }
      }
      
      // If we can't determine the format, try a generic conversion
      const dateObj = new Date(date);
      if (!isNaN(dateObj.getTime())) {
        return dateObj.toLocaleString();
      }
      
      return 'Invalid Date Format';
    } catch (error) {
      console.error('Error formatting date:', error, date);
      return 'Date Error';
    }
  };

  /**
   * Get the type of a date object for debugging purposes
   * @param date Any date format
   * @returns String describing the date type
   */
  const getDateType = (date: any): string => {
    if (!date) return 'undefined';
    
    if (typeof date === 'object') {
      if (date instanceof Date) return 'Date object';
      if ('toDate' in date && typeof date.toDate === 'function') return 'Firestore Timestamp';
      return `Object (${Object.prototype.toString.call(date)})`;
    }
    
    if (typeof date === 'number') {
      return `Number (${date < 10000000000 ? 'seconds' : 'milliseconds'})`;
    }
    
    return typeof date;
  };

  /**
   * Safely compares two dates for sorting, handling null/undefined values
   * @param a First object with date property
   * @param b Second object with date property
   * @param dateField The name of the date field to compare
   * @param ascending Whether to sort in ascending order (oldest first) or descending (newest first)
   * @returns Comparison result: negative if a comes before b, positive if a comes after b, 0 if equal
   */
  const compareDates = <T extends Record<string, any>>(
    a: T, 
    b: T, 
    dateField: keyof T, 
    ascending: boolean = false
  ): number => {
    // Safely get date values, handling various possible types
    const getDateValue = (obj: T, field: keyof T): Date | null => {
      const value = obj[field];
      if (!value) return null;
      
      try {
        // If it's already a Date object
        if (Object.prototype.toString.call(value) === '[object Date]') {
          return value as Date;
        }
        
        // If it's a Firestore timestamp with toDate() method
        if (typeof value === 'object' && 'toDate' in value && typeof (value as any).toDate === 'function') {
          return (value as any).toDate();
        }
        
        // If it's a string or number, try to convert to Date
        const date = new Date(value as any);
        return isNaN(date.getTime()) ? null : date;
      } catch {
        return null;
      }
    };
    
    const dateA = getDateValue(a, dateField);
    const dateB = getDateValue(b, dateField);
    
    // Handle cases where either date might be null/undefined
    if (!dateA && !dateB) return 0;
    if (!dateA) return ascending ? -1 : 1;
    if (!dateB) return ascending ? 1 : -1;
    
    // Normal comparison when both dates exist
    const comparison = dateA.getTime() - dateB.getTime();
    return ascending ? comparison : -comparison; // Reverse for descending order (newest first)
  };

  return {
    formatDate,
    getDateType,
    compareDates
  };
}
